import React, { useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import { AsyncStatus } from 'Models';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import { PermissionCodesV3 } from 'Models/Authorization/permissionCodesV3';
import { buildEndpoint, defaultDataTransform, useApiWorker, useDataFetcher, useDataPoster, useHasPermissionsV3 } from 'Utilities';
import { ROUTES } from 'Navigation';
import ReviewerProfileHeader, { ReviewerProfileHeaderSkeleton, ReviewerProfileTab } from './ReviewerProfile.Header';
import ReviewerProfileOverview, { ReviewerProfileOverviewSkeleton } from './ReviewerProfile.Overview';
import ReviewerProfileDocuments from './ReviewerProfile.Documents';
import ReviewerProfileNotes from './ReviewerProfile.Notes';
import {
  DEFAULT_REVIEWER_PROFILE,
  ReviewerProfileModel,
  useReviewerProfileForm,
} from './useReviewerProfileForm';
import { ContactInformationData } from './ReviewerProfile.Overview.ContactInformation';
import { BoardCertification, LicenseInformationData, StateLicense } from './ReviewerProfile.Overview.LicenseInformation';
import { RatesData } from './ReviewerProfile.Overview.Rates';
import { ReviewerDocumentRow } from './ReviewerProfile.Documents.Drawer';
import { ArchiveReasonOption } from './ReviewerProfile.ArchiveDialog';

const EMPTY_CONTACT: ContactInformationData = {};
const EMPTY_LICENSE: LicenseInformationData = {};
const EMPTY_RATES: RatesData = {};
const EMPTY_DOCUMENTS: ReviewerDocumentRow[] = [];
const EMPTY_ARCHIVE_REASONS: ArchiveReasonOption[] = [];

const ReviewerProfile: React.FC = () => {
  const { id, tab } = useParams<{ id: string; tab?: string }>();
  const history = useHistory();
  const [fieldErrors, setFieldErrors] = useState<{ fieldId: string; error: string }[]>([]);

  const activeTab: ReviewerProfileTab =
    (tab as ReviewerProfileTab) ?? ReviewerProfileTab.Overview;

  const [reviewer, loadingStatus, { getData: refetchReviewer, setData: setReviewerData }] = useDataFetcher<ReviewerProfileModel>(
    buildEndpoint(ENDPOINTS.reviewers.base, id),
    DEFAULT_REVIEWER_PROFILE,
    defaultDataTransform,
    undefined,
    true
  );

  const [contactData, contactLoadingStatus, { setData: setContactData }] = useDataFetcher<ContactInformationData>(
    buildEndpoint(ENDPOINTS.reviewers.base, id, 'contactInformation'),
    EMPTY_CONTACT,
    defaultDataTransform,
    undefined,
    !!id,
    '3.0'
  );

  const [licenseData, licenseLoadingStatus, { getData: refetchLicense, setData: setLicenseData }] = useDataFetcher<LicenseInformationData>(
    buildEndpoint(ENDPOINTS.reviewers.base, id, 'licenseInformation'),
    EMPTY_LICENSE,
    defaultDataTransform,
    undefined,
    !!id,
    '3.0'
  );

  // Fetched at this level (rather than only inside the Overview tab) so header-level actions
  // like Invite can validate Cosigner/required-documents regardless of the active tab.
  const [ratesData, , { getData: refetchRates }] = useDataFetcher<RatesData>(
    buildEndpoint(ENDPOINTS.reviewers.base, id, 'rates'),
    EMPTY_RATES,
    defaultDataTransform,
    undefined,
    !!id,
    '3.0'
  );

  const [documents, documentsLoadingStatus, { getData: refetchDocuments, setData: setDocuments }] = useDataFetcher<ReviewerDocumentRow[]>(
    buildEndpoint(ENDPOINTS.reviewers.base, id, 'documents'),
    EMPTY_DOCUMENTS,
    defaultDataTransform,
    undefined,
    !!id,
    '3.0'
  );

  const isDocumentsLoading = documentsLoadingStatus === AsyncStatus.Idle || documentsLoadingStatus === AsyncStatus.Pending;

  const uploadedDocumentTypeNames = useMemo(
    () => (documents ?? [])
      .map((document) => document.documentType?.name)
      .filter((name): name is string => !!name),
    [documents]
  );

  // Fetched once at page load (not lazily when the Archive dialog opens) so the modal never
  // shows an empty options list while waiting on this request.
  const [archiveReasons] = useDataFetcher<ArchiveReasonOption[]>(
    buildEndpoint(ENDPOINTS.reviewers.base, 'archiveReasons'),
    EMPTY_ARCHIVE_REASONS,
    defaultDataTransform,
    undefined,
    true,
    '3.0'
  );

  // Background reviewer refresh: refetchReviewer() flips the page into its loading skeleton and
  // unmounts the active tab, which is wrong right after a drawer save.
  const reviewerAPI = useApiWorker();
  const refreshReviewerSilently = async () => {
    try {
      const resp = await reviewerAPI.get(buildEndpoint(ENDPOINTS.reviewers.base, id));
      if (resp?.data) setReviewerData(defaultDataTransform(resp));
    } catch {
      /* the header keeps its current state; a reload still shows the truth */
    }
  };

  // License/certification drawers persist on their own; patch the page-level license data so the
  // saved credentials survive a tab switch, and refetch documents since their files are listed there.
  // The save can also auto-lift an expired-credentials hold server-side, so refetch the reviewer too
  // or the header keeps showing On Hold until a reload (STRAD2-2671 / TC-077).
  const handleCredentialsSaved = (stateLicenses: StateLicense[], boardCertifications: BoardCertification[]) => {
    setLicenseData((prev) => ({ ...prev, stateLicenses, boardCertifications }));
    refetchDocuments();
    refreshReviewerSilently();
  };

  const isContactLoading = contactLoadingStatus === AsyncStatus.Idle || contactLoadingStatus === AsyncStatus.Pending;
  const isLicenseLoading = licenseLoadingStatus === AsyncStatus.Idle || licenseLoadingStatus === AsyncStatus.Pending;

  // Optimistic update: Contact Information save already tells us the new firstName/lastName/etc.,
  // so patch local state directly instead of waiting on a refetch round-trip.
  const handleContactSaved = (updated: ContactInformationData) => {
    setContactData((prev) => ({ ...prev, ...updated }));
    setReviewerData((prev) => ({
      ...prev,
      firstName: updated.firstName ?? prev.firstName,
      lastName: updated.lastName ?? prev.lastName,
    }));
  };

  const handleResponse = (response: any) => {
    if (response.status >= 200 && response.status < 300) {
      toast.success('Reviewer profile has been updated');
    } else {
      toast.error('An error occurred');
    }
  };

  const handleError = (error: AxiosError) => {
    if (error.response?.status === 400) {
      toast.error('Please validate the highlighted fields');
      const errors = Object.entries((error.response?.data as any).errors).map(
        ([key, value]) => ({ fieldId: key, error: (value as string[])[0] })
      );
      setFieldErrors(errors);
    }
  };

  const [postData] = useDataPoster(
    buildEndpoint(ENDPOINTS.reviewers.base),
    handleResponse,
    false,
    undefined,
    handleError
  );

  const onSubmit = async (values: ReviewerProfileModel) => {
    if (!form.isValid) return;
    const response: any = await postData(values);
    if (response?.status === 200) history.goBack();
  };

  const hasPermission = useHasPermissionsV3();
  const form = useReviewerProfileForm({ onSubmit, reviewer });

  const renderTabContent = () => {
    switch (activeTab) {
      case ReviewerProfileTab.Overview:
        return (
          <ReviewerProfileOverview
            reviewer={reviewer}
            contactData={contactData}
            onContactSaved={handleContactSaved}
            isContactLoading={isContactLoading}
            licenseData={licenseData}
            onLicenseRefetch={refetchLicense}
            onCredentialsSaved={handleCredentialsSaved}
            isLicenseLoading={isLicenseLoading}
            ratesData={ratesData}
            onRatesRefetch={refetchRates}
            documents={documents}
          />
        );
      case ReviewerProfileTab.Documents:
        if (!hasPermission(PermissionCodesV3.Reviewer.Document.View)) {
          history.replace(`${ROUTES.REVIEWER_PROFILE}/${id}/${ReviewerProfileTab.Overview}`);
          return null;
        }
        return (
          <ReviewerProfileDocuments
            documents={documents}
            setDocuments={setDocuments}
            isLoading={isDocumentsLoading}
          />
        );
      case ReviewerProfileTab.Notes:
        if (!hasPermission(PermissionCodesV3.Reviewer.Note.View)) {
          history.replace(`${ROUTES.REVIEWER_PROFILE}/${id}/${ReviewerProfileTab.Overview}`);
          return null;
        }
        return <ReviewerProfileNotes />;
      default:
        return (
          <ReviewerProfileOverview
            reviewer={reviewer}
            contactData={contactData}
            onContactSaved={handleContactSaved}
            isContactLoading={isContactLoading}
            licenseData={licenseData}
            onLicenseRefetch={refetchLicense}
            onCredentialsSaved={handleCredentialsSaved}
            isLicenseLoading={isLicenseLoading}
            ratesData={ratesData}
            onRatesRefetch={refetchRates}
            documents={documents}
          />
        );
    }
  };

  const isLoading = loadingStatus === AsyncStatus.Idle || loadingStatus === AsyncStatus.Pending;

  return (
    <div>
      {isLoading
        ? <ReviewerProfileHeaderSkeleton />
        : (
          <ReviewerProfileHeader
            reviewer={reviewer}
            contactData={contactData}
            licenseData={licenseData}
            ratesData={ratesData}
            uploadedDocumentTypeNames={uploadedDocumentTypeNames}
            archiveReasons={archiveReasons}
            onReviewerRefetch={refetchReviewer}
          />
        )}
      {isLoading ? <ReviewerProfileOverviewSkeleton /> : renderTabContent()}
    </div>
  );
};

export default ReviewerProfile;
